import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { FaPlus, FaTrash, FaEdit, FaCheck, FaTimes } from 'react-icons/fa';
import { useBaseComponentFullscreen } from './BaseComponent';

// Debounce utility function
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export default function FlowDiagramComponent({ component, onUpdate }) {
  const isFullscreen = useBaseComponentFullscreen();
  const data = component.data || { nodes: [] };
  const { nodes = [] } = data;
  
  const [editingNode, setEditingNode] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null); // For connection mode
  const [selectedConnection, setSelectedConnection] = useState(null); // For connection selection
  const [actionNode, setActionNode] = useState(null); // For showing action menu
  const [connectionToDelete, setConnectionToDelete] = useState(null); // For showing red shaking connection
  const [hoveredConnectionToDelete, setHoveredConnectionToDelete] = useState(null); // For hover red effect
  const [previewConnection, setPreviewConnection] = useState(null); // For showing green preview connection
  const [expandedNode, setExpandedNode] = useState(null); // For expanded edit mode
  const [expandedConnection, setExpandedConnection] = useState(null); // For expanded connection modal
  const [editingNodeName, setEditingNodeName] = useState(''); // For editing node name in expanded mode
  const [editingConnectionName, setEditingConnectionName] = useState(null); // For editing connection names
  const [localConnectionLabel, setLocalConnectionLabel] = useState(''); // Local state for connection label input
  const [localNodeName, setLocalNodeName] = useState(''); // Local state for node name input
  const [newNodeName, setNewNodeName] = useState('');
  const [showAddNodeModal, setShowAddNodeModal] = useState(false); // For add node modal
  const [newNodeModalName, setNewNodeModalName] = useState(''); // For new node modal input
  const [containerWidth, setContainerWidth] = useState(800);
  const [componentWidth, setComponentWidth] = useState(800); // Track the full component width
  const [draggedNode, setDraggedNode] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hoveredConnection, setHoveredConnection] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const containerRef = useRef(null);
  const componentRef = useRef(null); // Reference to the full component

  // Debounced values for efficient updates
  const debouncedConnectionLabel = useDebounce(localConnectionLabel, 500);
  const debouncedNodeName = useDebounce(localNodeName, 500);

  // Update connection label when debounced value changes
  useEffect(() => {
    if (expandedConnection && debouncedConnectionLabel !== '') {
      const { fromNodeId, toNodeId } = expandedConnection;
      const currentLabel = nodes.find(n => n.id === fromNodeId)?.connections?.[toNodeId] || '';
      
      if (debouncedConnectionLabel !== currentLabel) {
        const updatedNodes = nodes.map(n => {
          if (n.id === fromNodeId) {
            return {
              ...n,
              connections: {
                ...n.connections,
                [toNodeId]: debouncedConnectionLabel
              }
            };
          }
          return n;
        });
        updateData({ ...data, nodes: updatedNodes });
      }
    }
  }, [debouncedConnectionLabel, expandedConnection, nodes, data]);

  // Update node name when debounced value changes
  useEffect(() => {
    if (expandedNode && debouncedNodeName !== '' && editingNodeName) {
      const currentName = nodes.find(n => n.id === expandedNode)?.name || '';
      
      if (debouncedNodeName !== currentName) {
        const updatedNodes = nodes.map(node => 
          node.id === expandedNode ? { ...node, name: debouncedNodeName } : node
        );
        updateData({ ...data, nodes: updatedNodes });
      }
    }
  }, [debouncedNodeName, expandedNode, editingNodeName, nodes, data]);

  // Monitor component resize using ResizeObserver for accurate repositioning
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        const newComponentWidth = width;
        const newContainerWidth = containerRef.current ? containerRef.current.offsetWidth : width * 0.67;
        
        setComponentWidth(newComponentWidth);
        setContainerWidth(newContainerWidth);
        
        // Trigger layout recalculation when size changes significantly
        const widthDiff = Math.abs(newContainerWidth - containerWidth);
        if (widthDiff > 50 && nodes.length > 0) {
          const hasCustomPositions = nodes.some(n => n.customX !== undefined || n.customY !== undefined);
          if (!hasCustomPositions) {
            // Force re-render by updating state to trigger layout recalc
            setNewNodeName(prev => prev + '');
          }
        }
      }
    });

    if (componentRef.current) {
      resizeObserver.observe(componentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerWidth, nodes]);

  const generateId = () => Math.random().toString(36).substr(2, 9);

  const updateData = (newData) => {
    onUpdate({ ...component, data: newData });
  };

  // Smart branching layout: like git sourcetree with intelligent connection ordering
  const layoutNodes = useMemo(() => {
    if (nodes.length === 0) return [];

    // Temporarily disable custom positions to fix broken layout
    // Check if nodes have custom positions
    // const hasCustomPositions = nodes.some(n => n.customX !== undefined || n.customY !== undefined);
    
    // if (hasCustomPositions) {
    //   // Use custom positions when available
    //   return nodes.map(node => ({
    //     ...node,
    //     x: node.customX || 0,
    //     y: node.customY || 0,
    //     level: Math.floor((node.customY || 0) / 100),
    //     column: Math.floor((node.customX || 0) / 80)
    //   }));
    // }

    // Always use intelligent git-style layout for now
    const nodeMap = new Map(nodes.map(n => [n.id, { ...n, level: 0, column: 0, x: 0, y: 0, priority: 0 }]));
    
    // Calculate node priorities based on multiple factors
    const calculateNodePriority = (node) => {
      let priority = 0;
      
      // Factor 1: Node name (alphabetical for consistency)
      priority += node.name.toLowerCase().charCodeAt(0) * 0.1;
      
      // Factor 2: Number of outgoing connections (more connections = higher priority)
      priority += node.connected_to.length * 10;
      
      // Factor 3: Node creation order (earlier nodes get slight priority)
      const nodeIndex = nodes.findIndex(n => n.id === node.id);
      priority += (nodes.length - nodeIndex) * 2;
      
      // Factor 4: Semantic analysis of node names for logical ordering
      const name = node.name.toLowerCase();
      
      // Start/initialization keywords get highest priority
      if (name.includes('start') || name.includes('init') || name.includes('begin') || 
          name.includes('setup') || name.includes('config') || name.includes('entry')) {
        priority += 100;
      }
      
      // Process/action keywords get medium-high priority
      if (name.includes('process') || name.includes('handle') || name.includes('execute') || 
          name.includes('run') || name.includes('do') || name.includes('perform')) {
        priority += 50;
      }
      
      // Decision/condition keywords get medium priority
      if (name.includes('check') || name.includes('validate') || name.includes('verify') || 
          name.includes('if') || name.includes('when') || name.includes('decide')) {
        priority += 30;
      }
      
      // End/completion keywords get lower priority
      if (name.includes('end') || name.includes('finish') || name.includes('complete') || 
          name.includes('stop') || name.includes('exit') || name.includes('final')) {
        priority -= 50;
      }
      
      return priority;
    };

    // Calculate priorities for all nodes
    nodes.forEach(node => {
      const layoutNode = nodeMap.get(node.id);
      layoutNode.priority = calculateNodePriority(node);
    });
    
    // Find root nodes and sort them by priority
    const incomingConnections = new Map();
    nodes.forEach(node => {
      incomingConnections.set(node.id, []);
    });
    
    nodes.forEach(node => {
      node.connected_to.forEach(targetId => {
        if (incomingConnections.has(targetId)) {
          incomingConnections.get(targetId).push(node.id);
        }
      });
    });

    const roots = nodes.filter(node => incomingConnections.get(node.id).length === 0)
      .sort((a, b) => nodeMap.get(b.id).priority - nodeMap.get(a.id).priority);
      
    if (roots.length === 0 && nodes.length > 0) {
      // If no true roots, find the node with highest priority
      const highestPriorityNode = nodes.reduce((max, node) => 
        nodeMap.get(node.id).priority > nodeMap.get(max.id).priority ? node : max
      );
      roots.push(highestPriorityNode);
    }

    // Assign levels using breadth-first traversal with smart ordering
    const visited = new Set();
    const queue = roots.map(root => ({ node: root, level: 0 }));
    const levelGroups = new Map(); // level -> array of nodes

    while (queue.length > 0) {
      const { node, level } = queue.shift();
      
      if (visited.has(node.id)) continue;
      visited.add(node.id);

      const layoutNode = nodeMap.get(node.id);
      layoutNode.level = level;

      // Group by level
      if (!levelGroups.has(level)) {
        levelGroups.set(level, []);
      }
      levelGroups.get(level).push(layoutNode);

      // Sort children by priority before adding to queue
      const children = node.connected_to
        .map(targetId => nodes.find(n => n.id === targetId))
        .filter(targetNode => targetNode && !visited.has(targetNode.id))
        .sort((a, b) => nodeMap.get(b.id).priority - nodeMap.get(a.id).priority);

      children.forEach(targetNode => {
        queue.push({ node: targetNode, level: level + 1 });
      });
    }

    // Handle remaining unvisited nodes, sorted by priority
    const unvisitedNodes = nodes.filter(node => !visited.has(node.id))
      .sort((a, b) => nodeMap.get(b.id).priority - nodeMap.get(a.id).priority);
      
    unvisitedNodes.forEach(node => {
      const layoutNode = nodeMap.get(node.id);
      layoutNode.level = 0;
      if (!levelGroups.has(0)) {
        levelGroups.set(0, []);
      }
      levelGroups.get(0).push(layoutNode);
    });

    // Position nodes with intelligent crossing reduction
    const LEVEL_HEIGHT = 120;
    const COLUMN_WIDTH = 100; // Increased from 100 to 140 for more horizontal space
    
    // Sort levels for processing
    const sortedLevels = Array.from(levelGroups.keys()).sort((a, b) => a - b);
    
    sortedLevels.forEach((level, levelIndex) => {
      const nodesAtLevel = levelGroups.get(level);
      
      if (levelIndex === 0) {
        // Root level - sort by priority
        nodesAtLevel.sort((a, b) => b.priority - a.priority);
      } else {
        // For subsequent levels, balance parent position and priority
        nodesAtLevel.forEach(node => {
          const parents = incomingConnections.get(node.id) || [];
          if (parents.length > 0) {
            const parentNodes = parents.map(pid => nodeMap.get(pid)).filter(Boolean);
            node.avgParentX = parentNodes.length > 0 
              ? parentNodes.reduce((sum, parent) => sum + (parent.x || 0), 0) / parentNodes.length 
              : 0;
            // Weight parent position more heavily, but still consider priority
            node.sortScore = node.avgParentX * 2 + node.priority * 0.1;
          } else {
            node.avgParentX = 0;
            node.sortScore = node.priority * 0.1;
          }
        });
        
        // Sort by combined score (parent position + priority)
        nodesAtLevel.sort((a, b) => a.sortScore - b.sortScore);
      }
      
      // Assign positions with better spacing for readability
      const totalWidth = Math.max((nodesAtLevel.length - 1) * COLUMN_WIDTH, 0);
      const startX = -totalWidth / 2;
      
      nodesAtLevel.forEach((node, index) => {
        node.x = startX + index * COLUMN_WIDTH;
        node.y = level * LEVEL_HEIGHT;
        node.column = index;
      });
    });

    return Array.from(nodeMap.values());
  }, [nodes]);

  // Add new node
  const addNode = () => {
    if (!newNodeName.trim()) return;

    const newNode = {
      id: generateId(),
      name: newNodeName.trim(),
      connected_to: []
    };

    updateData({
      ...data,
      nodes: [...nodes, newNode]
    });

    setNewNodeName('');
  };

  // Delete node
  const deleteNode = (nodeId) => {
    updateData({
      ...data,
      nodes: nodes.filter(n => n.id !== nodeId).map(node => ({
        ...node,
        connected_to: node.connected_to.filter(id => id !== nodeId)
      }))
    });
  };

  // Update node name
  const updateNodeName = (nodeId, newName) => {
    updateData({
      ...data,
      nodes: nodes.map(node => 
        node.id === nodeId ? { ...node, name: newName } : node
      )
    });
    setEditingNode(null);
    setExpandedNode(null);
    setEditingNodeName('');
  };

  // Update connection name
  const updateConnectionName = (fromNodeId, toNodeId, connectionName) => {
    updateData({
      ...data,
      nodes: nodes.map(node => {
        if (node.id === fromNodeId) {
          const updatedConnections = node.connections || {};
          return {
            ...node,
            connections: {
              ...updatedConnections,
              [toNodeId]: connectionName
            }
          };
        }
        return node;
      })
    });
  };

  // Auto-optimize layout to reduce crossings
  const optimizeLayout = () => {
    // Reset custom positions to trigger automatic layout
    const optimizedNodes = nodes.map(node => {
      const { customX, customY, ...cleanNode } = node;
      return cleanNode;
    });
    
    updateData({
      ...data,
      nodes: optimizedNodes
    });
  };

  // Node selection and connection logic
  const handleNodeClick = (e, node) => {
    e.stopPropagation(); // Prevent deselection
    
    if (draggedNode) return; // Don't handle clicks during drag
    
    // Clear preview connection on any click
    setPreviewConnection(null);
    
    if (isFullscreen) {
      // In fullscreen mode, support connection creation first
      if (selectedNode === null) {
        // No node selected - select this node for connection mode AND show details
        setSelectedNode(node.id);
        setEditingNode(node.id);
        setLocalNodeName(node.name); // Initialize local state
        setSelectedConnection(null);
        setExpandedNode(null);
      } else if (selectedNode === node.id) {
        // Clicking same node again - open modal
        setExpandedNode(node.id);
        setEditingNodeName(node.name);
        setLocalNodeName(node.name); // Initialize local state
        setSelectedNode(null);
        setEditingNode(null);
        setSelectedConnection(null);
      } else {
        // Different node selected - create/toggle connection, keep first node selected
        toggleConnection(selectedNode, node.id);
        // Automatically deselect after connection action
        setSelectedNode(null);
        setEditingNode(null);
      }
    } else {
      // In compact mode: connection creation first, then edit mode logic
      if (selectedNode === null && editingNode === null) {
        // First click - select for connections
        setSelectedNode(node.id);
        setEditingNode(node.id);
        setLocalNodeName(node.name); // Initialize local state
        setActionNode(null);
        setSelectedConnection(null);
        setExpandedNode(null);
        setEditingNodeName('');
        setEditingConnectionName(null);
      } else if (selectedNode === node.id && editingNode === node.id) {
        // Second click on same node - open modal
        setExpandedNode(node.id);
        setEditingNodeName(node.name);
        setLocalNodeName(node.name); // Initialize local state
        setEditingNode(null);
        setSelectedNode(null);
        setActionNode(null);
        setSelectedConnection(null);
      } else if (selectedNode !== null && selectedNode !== node.id) {
        // Different node while one is selected - create/toggle connection
        toggleConnection(selectedNode, node.id);
        // Automatically deselect after connection action
        setSelectedNode(null);
        setEditingNode(null);
      } else if (editingNode !== node.id) {
        // Switch to editing different node
        setEditingNode(node.id);
        setSelectedNode(node.id);
        setLocalNodeName(node.name); // Initialize local state
        setActionNode(null);
        setSelectedConnection(null);
        setExpandedNode(null);
        setEditingNodeName('');
        setEditingConnectionName(null);
      }
    }
  };

  // Handle clicking outside nodes to deselect
  const handleContainerClick = (e) => {
    // Only deselect if clicking directly on the container, not on child elements
    if (e.target === e.currentTarget) {
      setSelectedNode(null);
      setSelectedConnection(null);
      setActionNode(null);
      setEditingNode(null);
      setConnectionToDelete(null);
      setHoveredConnectionToDelete(null);
      setPreviewConnection(null);
      setExpandedNode(null);
      setExpandedConnection(null);
      setEditingNodeName('');
      setEditingConnectionName(null);
      setLocalConnectionLabel(''); // Clear local state
      setLocalNodeName(''); // Clear local state
      setShowAddNodeModal(false); // Clear add node modal
      setNewNodeModalName(''); // Clear add node modal name
    }
  };

  // Action menu functions
  const deleteNodeAndConnections = (nodeId) => {
    const updatedNodes = nodes.filter(node => {
      if (node.id === nodeId) return false; // Remove the node itself
      // Remove connections to the deleted node
      return {
        ...node,
        connected_to: node.connected_to.filter(id => id !== nodeId)
      };
    }).map(node => ({
      ...node,
      connected_to: node.connected_to.filter(id => id !== nodeId)
    }));
    
    updateData({
      ...data,
      nodes: updatedNodes
    });
    setActionNode(null);
  };

  // Drag functionality
  const handleMouseDown = (e, node) => {
    if (e.button !== 0) return; // Only left mouse button
    
    const rect = containerRef.current.getBoundingClientRect();
    const nodeElement = e.currentTarget;
    const nodeRect = nodeElement.getBoundingClientRect();
    
    setDraggedNode(node.id);
    setDragOffset({
      x: e.clientX - nodeRect.left - 32, // 32 = half of 64px node width
      y: e.clientY - nodeRect.top - 32   // 32 = half of 64px node height
    });
    
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!draggedNode || !containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const relativeX = e.clientX - rect.left - containerWidth / 2 - dragOffset.x;
    const relativeY = e.clientY - rect.top - 20 - dragOffset.y;
    
    // Update node position
    const updatedNodes = nodes.map(node => {
      if (node.id === draggedNode) {
        return {
          ...node,
          customX: relativeX,
          customY: Math.max(0, relativeY) // Prevent negative Y
        };
      }
      return node;
    });
    
    updateData({
      ...data,
      nodes: updatedNodes
    });
  };

  const handleMouseUp = () => {
    setDraggedNode(null);
    setDragOffset({ x: 0, y: 0 });
  };

  // Add mouse event listeners
  useEffect(() => {
    if (draggedNode) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [draggedNode, dragOffset, containerWidth, nodes]);

  // Handle connection click
  const handleConnectionClick = (e, fromNodeId, toNodeId) => {
    e.stopPropagation();
    const connectionId = `${fromNodeId}-${toNodeId}`;
    
    // Get current connection label to initialize local state
    const currentLabel = nodes.find(n => n.id === fromNodeId)?.connections?.[toNodeId] || '';
    setLocalConnectionLabel(currentLabel);
    
    // Open connection modal immediately
    setExpandedConnection({ fromNodeId, toNodeId, connectionId });
    setSelectedConnection(null);
    setSelectedNode(null);
    setExpandedNode(null);
    setEditingNode(null);
  };

  // Delete connection
  const deleteConnection = (fromNodeId, toNodeId) => {
    updateData({
      ...data,
      nodes: nodes.map(node => {
        if (node.id === fromNodeId) {
          return {
            ...node,
            connected_to: node.connected_to.filter(id => id !== toNodeId),
            connections: node.connections ? 
              Object.fromEntries(Object.entries(node.connections).filter(([key]) => key !== toNodeId)) : 
              undefined
          };
        }
        return node;
      })
    });
    setSelectedConnection(null);
  };

  // Toggle connection between nodes
  const toggleConnection = (fromNodeId, toNodeId) => {
    updateData({
      ...data,
      nodes: nodes.map(node => {
        if (node.id === fromNodeId) {
          const isConnected = node.connected_to.includes(toNodeId);
          return {
            ...node,
            connected_to: isConnected 
              ? node.connected_to.filter(id => id !== toNodeId)
              : [...node.connected_to, toNodeId]
          };
        }
        return node;
      })
    });
  };

  const showSidebar = componentWidth > 800 || isFullscreen; // Always show sidebar in fullscreen mode

  return (
    <div 
      ref={componentRef}
      className={`h-full flex bg-[var(--bg-primary)] ${isFullscreen ? 'p-6' : 'p-4'}`}
    >
      {/* Left: Flow Visualization */}
      <div className={`flex flex-col ${showSidebar ? 'w-2/3 pr-4 border-r border-[var(--border-color)]' : 'w-full'}`}>
        {!showSidebar && (
          <div className="mb-4">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Node name..."
                value={newNodeName}
                onChange={(e) => setNewNodeName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addNode()}
                className="flex-1 px-3 py-2 border border-[var(--border-color)] rounded-md bg-[var(--bg-secondary)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
              />
              <button
                onClick={addNode}
                disabled={!newNodeName.trim()}
                className="px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-opacity"
                style={{ 
                  backgroundColor: 'var(--primary)', 
                  color: 'var(--bg-primary)' 
                }}
                title="Add node"
              >
                <FaPlus className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}

        {/* Visual Flow Only */}
        <div className="flex-1 relative overflow-auto min-h-0">
          {layoutNodes.length === 0 ? (
            <div className="text-center py-12 text-[var(--text-secondary)]">
              <div className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-dashed border-[var(--border-color)] flex items-center justify-center">
                <FaPlus className="w-4 h-4" />
              </div>
              <p>Add your first node to get started!</p>
            </div>
          ) : (
            <div 
              ref={containerRef}
              className="relative cursor-pointer w-full" 
              onClick={handleContainerClick}
              style={{ 
                minWidth: '100%',
                minHeight: `${Math.max(...layoutNodes.map(n => n.y)) + 120}px`,
                height: `${Math.max(...layoutNodes.map(n => n.y)) + 120}px`,
                paddingLeft: '50%', // Center using percentage instead of fixed pixels
                paddingTop: '20px',
                paddingBottom: '20px'
              }}
            >
              {/* SVG for connection lines */}
              <svg 
                className="absolute inset-0 w-full h-full pointer-events-none"
                style={{ zIndex: 1 }}
              >
                {(() => {
                  // Track connections per direction to avoid overlaps
                  const connectionCounts = new Map();
                  const connectionIndexes = new Map();
                  
                  // First pass: count connections per node and direction with smart ordering
                  const sortedConnectionsByNode = new Map();
                  
                  layoutNodes.forEach((layoutNode) => {
                    // Sort connections by target direction for natural flow ordering - use original data
                    const originalNode = nodes.find(n => n.id === layoutNode.id);
                    if (!originalNode) return;
                    
                    const sortedConnections = [...originalNode.connected_to].sort((targetId1, targetId2) => {
                      const target1 = layoutNodes.find(n => n.id === targetId1);
                      const target2 = layoutNodes.find(n => n.id === targetId2);
                      if (!target1 || !target2) return 0;
                      
                      // Calculate directions for both targets
                      const fromCenterX = containerWidth / 2 + layoutNode.x;
                      const fromCenterY = layoutNode.y + 20 + 24;
                      
                      const to1CenterX = containerWidth / 2 + target1.x;
                      const to1CenterY = target1.y + 20 + 24;
                      const to2CenterX = containerWidth / 2 + target2.x;
                      const to2CenterY = target2.y + 20 + 24;
                      
                      const delta1X = to1CenterX - fromCenterX;
                      const delta1Y = to1CenterY - fromCenterY;
                      const delta2X = to2CenterX - fromCenterX;
                      const delta2Y = to2CenterY - fromCenterY;
                      
                      // Determine primary directions
                      let dir1, dir2;
                      if (Math.abs(delta1X) > Math.abs(delta1Y)) {
                        dir1 = delta1X > 0 ? 'right' : 'left';
                      } else {
                        dir1 = delta1Y > 0 ? 'down' : 'up';
                      }
                      
                      if (Math.abs(delta2X) > Math.abs(delta2Y)) {
                        dir2 = delta2X > 0 ? 'right' : 'left';
                      } else {
                        dir2 = delta2Y > 0 ? 'down' : 'up';
                      }
                      
                      // Direction priority: right > down > left > up (clockwise from right)
                      const directionPriority = { 'right': 4, 'down': 3, 'left': 2, 'up': 1 };
                      const priority1 = directionPriority[dir1] || 0;
                      const priority2 = directionPriority[dir2] || 0;
                      
                      if (priority1 !== priority2) {
                        return priority2 - priority1; // Higher priority first
                      }
                      
                      // If same direction, sort by angle within that quadrant for smooth visual flow
                      const angle1 = Math.atan2(delta1Y, delta1X);
                      const angle2 = Math.atan2(delta2Y, delta2X);
                      
                      // For right/left: sort by Y position (top to bottom)
                      if (dir1 === 'right' || dir1 === 'left') {
                        return delta1Y - delta2Y;
                      }
                      // For up/down: sort by X position (left to right)
                      else {
                        return delta1X - delta2X;
                      }
                    });
                    
                    // Store sorted connections on the layout node (not original data)
                    layoutNode.connected_to = sortedConnections;
                    
                    sortedConnections.forEach(targetId => {
                      const targetNode = layoutNodes.find(n => n.id === targetId);
                      if (!targetNode) return;
                      
                      const fromCenterX = containerWidth / 2 + layoutNode.x;
                      const fromCenterY = layoutNode.y + 20 + 24;
                      const toCenterX = containerWidth / 2 + targetNode.x;
                      const toCenterY = targetNode.y + 20 + 24;
                      
                      const deltaX = toCenterX - fromCenterX;
                      const deltaY = toCenterY - fromCenterY;
                      
                      // Determine directions based on actual movement direction
                      let fromDirection, toDirection;
                      
                      // Exit direction: where the line should EXIT from the source node
                      if (Math.abs(deltaX) > Math.abs(deltaY)) {
                        // Horizontal movement dominates
                        fromDirection = deltaX > 0 ? 'right' : 'left'; // Exit right if going right, left if going left
                        toDirection = deltaX > 0 ? 'left' : 'right';   // Enter from opposite side
                      } else {
                        // Vertical movement dominates  
                        fromDirection = deltaY > 0 ? 'down' : 'up';    // Exit down if going down, up if going up
                        toDirection = deltaY > 0 ? 'up' : 'down';      // Enter from opposite side
                      }
                      
                      // Separate counting for outgoing and incoming to avoid crossings
                      const fromKey = `${layoutNode.id}-out-${fromDirection}`;
                      const toKey = `${targetId}-in-${toDirection}`;
                      
                      connectionCounts.set(fromKey, (connectionCounts.get(fromKey) || 0) + 1);
                      connectionCounts.set(toKey, (connectionCounts.get(toKey) || 0) + 1);
                    });
                  });
                  
                  // Second pass: render connections with spacing
                  return layoutNodes.map((node) => 
                    node.connected_to.map(targetId => {
                      const targetNode = layoutNodes.find(n => n.id === targetId);
                      if (!targetNode) return null;
                      
                      // Calculate node centers
                      const fromCenterX = containerWidth / 2 + node.x;
                      const fromCenterY = node.y + 20 + 32; // Adjusted for 64px nodes (32 = half height)
                      const toCenterX = containerWidth / 2 + targetNode.x;
                      const toCenterY = targetNode.y + 20 + 32; // Adjusted for 64px nodes
                      
                      // Calculate optimal connection points at 90-degree angles with spacing
                      const deltaX = toCenterX - fromCenterX;
                      const deltaY = toCenterY - fromCenterY;
                      
                      let fromX, fromY, toX, toY;
                      let fromDirection, toDirection;
                      
                      const circleRadius = 32; // Updated to match 64px nodes (32 = half width)
                      const connectionSpacing = 8; // Space between parallel connections
                      
                      // Determine directions and get connection indexes for spacing
                      if (Math.abs(deltaX) > Math.abs(deltaY)) {
                        fromDirection = deltaX > 0 ? 'right' : 'left';
                        toDirection = deltaX > 0 ? 'left' : 'right';
                      } else {
                        fromDirection = deltaY > 0 ? 'down' : 'up';
                        toDirection = deltaY > 0 ? 'up' : 'down';
                      }
                      
                      // Get connection index for spacing - separate for in/out
                      const fromKey = `${node.id}-out-${fromDirection}`;
                      const toKey = `${targetId}-in-${toDirection}`;
                      
                      const fromIndex = connectionIndexes.get(fromKey) || 0;
                      const toIndex = connectionIndexes.get(toKey) || 0;
                      connectionIndexes.set(fromKey, fromIndex + 1);
                      connectionIndexes.set(toKey, toIndex + 1);
                      
                      const fromCount = connectionCounts.get(fromKey) || 1;
                      const toCount = connectionCounts.get(toKey) || 1;
                      
                      // Calculate offset for multiple connections - simpler approach
                      const fromOffset = fromCount > 1 ? (fromIndex - Math.floor(fromCount / 2)) * connectionSpacing : 0;
                      const toOffset = toCount > 1 ? (toIndex - Math.floor(toCount / 2)) * connectionSpacing : 0;
                      
                      // Calculate connection points with spacing
                      switch (fromDirection) {
                        case 'right':
                          fromX = fromCenterX + circleRadius;
                          fromY = fromCenterY + fromOffset;
                          break;
                        case 'left':
                          fromX = fromCenterX - circleRadius;
                          fromY = fromCenterY + fromOffset;
                          break;
                        case 'down':
                          fromX = fromCenterX + fromOffset;
                          fromY = fromCenterY + circleRadius;
                          break;
                        case 'up':
                          fromX = fromCenterX + fromOffset;
                          fromY = fromCenterY - circleRadius;
                          break;
                      }
                      
                      switch (toDirection) {
                        case 'right':
                          toX = toCenterX + circleRadius;
                          toY = toCenterY + toOffset;
                          break;
                        case 'left':
                          toX = toCenterX - circleRadius;
                          toY = toCenterY + toOffset;
                          break;
                        case 'down':
                          toX = toCenterX + toOffset;
                          toY = toCenterY + circleRadius;
                          break;
                        case 'up':
                          toX = toCenterX + toOffset;
                          toY = toCenterY - circleRadius;
                          break;
                      }
                      
                      // Create smooth curves between 90-degree connection points
                      let pathData;
                      
                      // Calculate the connection path distance
                      const connectionDistance = Math.sqrt((toX - fromX) ** 2 + (toY - fromY) ** 2);
                      const connectionId = `${node.id}-${targetId}`;
                      
                      // Determine if this connection should be highlighted
                      // Highlight connections when hovered directly OR when selected
                      const isHighlighted = hoveredConnection === connectionId || 
                                           selectedConnection === connectionId;
                      
                      // Check for nodes that would be crossed by a straight line
                      const crossedNodes = layoutNodes.filter(otherNode => {
                        if (otherNode.id === node.id || otherNode.id === targetId) return false;
                        
                        const otherX = containerWidth / 2 + otherNode.x;
                        const otherY = otherNode.y + 20 + 32; // Updated for 64px nodes
                        
                        // Check if this node is roughly in the path between source and target
                        const minX = Math.min(fromX, toX) - 30;
                        const maxX = Math.max(fromX, toX) + 30;
                        const minY = Math.min(fromY, toY) - 30;
                        const maxY = Math.max(fromY, toY) + 30;
                        
                        return otherX >= minX && otherX <= maxX && otherY >= minY && otherY <= maxY;
                      });
                      
                      if (connectionDistance < 50) {
                        // Very short connections - straight line
                        pathData = `M ${fromX} ${fromY} L ${toX} ${toY}`;
                      } else if (crossedNodes.length > 0 && connectionDistance > 150) {
                        // Long connections that cross nodes - use intelligent routing
                        const midX = (fromX + toX) / 2;
                        const midY = (fromY + toY) / 2;
                        
                        // Determine routing direction based on available space
                        let routeOffsetX = 0;
                        let routeOffsetY = 0;
                        
                        // Check if we should route around horizontally or vertically
                        const horizontalSpace = Math.abs(deltaX);
                        const verticalSpace = Math.abs(deltaY);
                        
                        if (horizontalSpace > verticalSpace) {
                          // Route vertically around obstacles
                          const routeDirection = deltaY >= 0 ? -1 : 1; // Route opposite to main direction
                          routeOffsetY = routeDirection * Math.min(80, connectionDistance * 0.3);
                        } else {
                          // Route horizontally around obstacles  
                          const routeDirection = deltaX >= 0 ? -1 : 1; // Route opposite to main direction
                          routeOffsetX = routeDirection * Math.min(80, connectionDistance * 0.3);
                        }
                        
                        // Create waypoints for routing around obstacles
                        const waypoint1X = fromX + (midX - fromX) * 0.3 + routeOffsetX;
                        const waypoint1Y = fromY + (midY - fromY) * 0.3 + routeOffsetY;
                        const waypoint2X = toX + (midX - toX) * 0.3 + routeOffsetX;
                        const waypoint2Y = toY + (midY - toY) * 0.3 + routeOffsetY;
                        
                        // Use smooth curve through waypoints
                        pathData = `M ${fromX} ${fromY} C ${waypoint1X} ${waypoint1Y}, ${waypoint2X} ${waypoint2Y}, ${toX} ${toY}`;
                      } else {
                        // Standard connections - smooth curves based on exit/entry directions
                        const curveLength = Math.min(connectionDistance * 0.4, 60); // Adaptive curve length
                        
                        // Calculate control points based on exit direction
                        let cp1X = fromX;
                        let cp1Y = fromY;
                        
                        switch (fromDirection) {
                          case 'right':
                            cp1X = fromX + curveLength;
                            break;
                          case 'left':
                            cp1X = fromX - curveLength;
                            break;
                          case 'down':
                            cp1Y = fromY + curveLength;
                            break;
                          case 'up':
                            cp1Y = fromY - curveLength;
                            break;
                        }
                        
                        // Calculate control points based on entry direction
                        let cp2X = toX;
                        let cp2Y = toY;
                        
                        switch (toDirection) {
                          case 'right':
                            cp2X = toX + curveLength;
                            break;
                          case 'left':
                            cp2X = toX - curveLength;
                            break;
                          case 'down':
                            cp2Y = toY + curveLength;
                            break;
                          case 'up':
                            cp2Y = toY - curveLength;
                            break;
                        }
                        
                        // Create smooth cubic bezier curve
                        pathData = `M ${fromX} ${fromY} C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${toX} ${toY}`;
                      }                    return (
                      <g key={connectionId}>
                        {/* Invisible wider path for easier hovering and clicking */}
                        <path
                          d={pathData}
                          stroke="transparent"
                          strokeWidth="15"
                          fill="none"
                          className="cursor-pointer"
                          style={{ pointerEvents: 'all' }}
                          onMouseEnter={() => setHoveredConnection(connectionId)}
                          onMouseLeave={() => setHoveredConnection(null)}
                          onClick={(e) => handleConnectionClick(e, node.id, targetId)}
                        />
                        
                        {/* Visual connection line with enhanced styling */}
                        {isHighlighted && (
                          <path
                            d={pathData}
                            strokeWidth="10"
                            fill="none"
                            className="pointer-events-none"
                            strokeLinecap="round"
                            style={{ stroke: 'var(--secondary)50' }}
                          />
                        )}
                        
                        <path
                          d={pathData}
                          stroke={connectionToDelete === connectionId || hoveredConnectionToDelete === connectionId ? "var(--danger)" : 
                                 selectedConnection === connectionId ? "var(--primary)" : "var(--secondary)"}
                          strokeWidth={isHighlighted || selectedConnection === connectionId ? "4" : "3"}
                          fill="none"
                          className={`transition-all duration-200 pointer-events-none ${
                            isHighlighted || selectedConnection === connectionId ? 'drop-shadow-lg' : ''
                          } ${(connectionToDelete === connectionId || hoveredConnectionToDelete === connectionId) ? 'animate-pulse' : ''}`}
                          style={{
                            filter: (connectionToDelete === connectionId || hoveredConnectionToDelete === connectionId) ? 'drop-shadow(0 0 8px var(--danger))' : 
                                   selectedConnection === connectionId ? 'drop-shadow(0 0 6px var(--primary))' : 'none'
                          }}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          // Fade connections when hovering nodes (unless it's part of hovered node) or other connections
                          opacity={hoveredNode && hoveredNode !== node.id && hoveredNode !== targetId ? 0.3 : 
                                  hoveredConnection && hoveredConnection !== connectionId && selectedConnection !== connectionId ? 0.3 : 1}
                        />
                        
                        {/* Arrow at middle of line */}
                        {(() => {
                          let midX, midY, angle;
                          
                          if (pathData.includes('C')) {
                            // For curved paths (Bézier curves), calculate actual curve middle
                            const pathParts = pathData.split(' ');
                            if (pathParts.length >= 7) {
                              // Extract curve control points: M x1 y1 C cp1x cp1y cp2x cp2y x2 y2
                              const x1 = parseFloat(pathParts[1]);
                              const y1 = parseFloat(pathParts[2]);
                              const cp1x = parseFloat(pathParts[4]);
                              const cp1y = parseFloat(pathParts[5].replace(',', ''));
                              const cp2x = parseFloat(pathParts[6]);
                              const cp2y = parseFloat(pathParts[7].replace(',', ''));
                              const x2 = parseFloat(pathParts[8]);
                              const y2 = parseFloat(pathParts[9]);
                              
                              // Calculate point at t=0.5 on the Bézier curve
                              const t = 0.5;
                              midX = Math.pow(1-t, 3) * x1 + 3 * Math.pow(1-t, 2) * t * cp1x + 3 * (1-t) * Math.pow(t, 2) * cp2x + Math.pow(t, 3) * x2;
                              midY = Math.pow(1-t, 3) * y1 + 3 * Math.pow(1-t, 2) * t * cp1y + 3 * (1-t) * Math.pow(t, 2) * cp2y + Math.pow(t, 3) * y2;
                              
                              // Calculate tangent direction at t=0.5
                              const tangentX = 3 * Math.pow(1-t, 2) * (cp1x - x1) + 6 * (1-t) * t * (cp2x - cp1x) + 3 * Math.pow(t, 2) * (x2 - cp2x);
                              const tangentY = 3 * Math.pow(1-t, 2) * (cp1y - y1) + 6 * (1-t) * t * (cp2y - cp1y) + 3 * Math.pow(t, 2) * (y2 - cp2y);
                              
                              angle = Math.atan2(tangentY, tangentX) * 180 / Math.PI;
                            } else {
                              // Fallback to simple middle calculation
                              midX = (fromX + toX) / 2;
                              midY = (fromY + toY) / 2;
                              angle = Math.atan2(toY - fromY, toX - fromX) * 180 / Math.PI;
                            }
                          } else {
                            // For straight lines, use simple middle calculation
                            midX = (fromX + toX) / 2;
                            midY = (fromY + toY) / 2;
                            angle = Math.atan2(toY - fromY, toX - fromX) * 180 / Math.PI;
                          }
                          
                          // Arrow size - made bigger
                          const arrowSize = 16;
                          const arrowHalfHeight = 6;
                          
                          return (
                            <polygon
                              points={`0,${-arrowHalfHeight} ${arrowSize},0 0,${arrowHalfHeight}`}
                              fill={connectionToDelete === connectionId || hoveredConnectionToDelete === connectionId ? "var(--danger)" : 
                                   selectedConnection === connectionId ? "var(--primary)" : "var(--secondary)"}
                              stroke={connectionToDelete === connectionId || hoveredConnectionToDelete === connectionId ? "var(--danger)" : 
                                     selectedConnection === connectionId ? "var(--primary)" : "var(--secondary)"}
                              strokeWidth="0.5"
                              transform={`translate(${midX}, ${midY}) rotate(${angle})`}
                              className={`transition-all duration-200 pointer-events-none ${
                                (connectionToDelete === connectionId || hoveredConnectionToDelete === connectionId) ? 'animate-pulse' : ''
                              }`}
                              style={{
                                filter: (connectionToDelete === connectionId || hoveredConnectionToDelete === connectionId) ? 'drop-shadow(0 0 4px var(--danger))' : 
                                       selectedConnection === connectionId ? 'drop-shadow(0 0 3px var(--primary))' : 'none',
                                opacity: hoveredNode && hoveredNode !== node.id && hoveredNode !== targetId ? 0.3 : 
                                        hoveredConnection && hoveredConnection !== connectionId && selectedConnection !== connectionId ? 0.3 : 1
                              }}
                            />
                          );
                        })()}
                        
                        {/* Connection label text */}
                        {(() => {
                          const originalNode = nodes.find(n => n.id === node.id);
                          const connectionLabel = originalNode?.connections?.[targetId];
                          
                          if (!connectionLabel) return null;
                          
                          // Calculate label position along the connection path, offset from the arrow
                          let labelX, labelY;
                          
                          if (pathData.includes('C')) {
                            // For curved paths, position label at t=0.7 (after the arrow at 0.5)
                            const parts = pathData.match(/M\s*([\d.-]+)\s*([\d.-]+)\s*C\s*([\d.-]+)\s*([\d.-]+),\s*([\d.-]+)\s*([\d.-]+),\s*([\d.-]+)\s*([\d.-]+)/);
                            if (parts) {
                              const [, x0, y0, x1, y1, x2, y2, x3, y3] = parts.map(Number);
                              // Calculate point at t=0.7 on cubic bezier curve (after the arrow at t=0.5)
                              const t = 0.7;
                              labelX = Math.pow(1-t, 3) * x0 + 3 * Math.pow(1-t, 2) * t * x1 + 3 * (1-t) * Math.pow(t, 2) * x2 + Math.pow(t, 3) * x3;
                              labelY = Math.pow(1-t, 3) * y0 + 3 * Math.pow(1-t, 2) * t * y1 + 3 * (1-t) * Math.pow(t, 2) * y2 + Math.pow(t, 3) * y3;
                            } else {
                              // Fallback for curved path parsing issues
                              labelX = fromX + (toX - fromX) * 0.7;
                              labelY = fromY + (toY - fromY) * 0.7;
                            }
                          } else {
                            // For straight lines, position at 70% along the line (after the arrow at 50%)
                            labelX = fromX + (toX - fromX) * 0.7;
                            labelY = fromY + (toY - fromY) * 0.7;
                          }
                          
                          return (
                            <g style={{ zIndex: isHighlighted ? 10 : 1 }}>
                              {/* Invisible clickable area for the label */}
                              <rect
                                x={labelX - connectionLabel.length * 3.5 - 2}
                                y={labelY - 10}
                                width={connectionLabel.length * 7 + 4}
                                height={20}
                                rx="3"
                                fill="transparent"
                                className="cursor-pointer"
                                style={{ pointerEvents: 'all', zIndex: isHighlighted ? 10 : 1 }}
                                onMouseEnter={() => setHoveredConnection(connectionId)}
                                onMouseLeave={() => setHoveredConnection(null)}
                                onClick={(e) => handleConnectionClick(e, node.id, targetId)}
                              />
                              
                              {/* Background rectangle for better readability */}
                              <rect
                                x={labelX - connectionLabel.length * 3.5}
                                y={labelY - 8}
                                width={connectionLabel.length * 7}
                                height={16}
                                rx="3"
                                fill="var(--bg-primary)"
                                stroke="var(--border-color)"
                                strokeWidth="1"
                                className="transition-all duration-200 pointer-events-none"
                                style={{
                                  opacity: isHighlighted || selectedConnection === connectionId ? 1 : 0.9,
                                  filter: selectedConnection === connectionId ? 'drop-shadow(0 2px 4px var(--primary)30)' : 
                                         hoveredConnection === connectionId ? 'drop-shadow(0 1px 3px var(--secondary)30)' : 'none',
                                  strokeWidth: selectedConnection === connectionId ? '2' : 
                                              hoveredConnection === connectionId ? '1.5' : '1',
                                  zIndex: isHighlighted ? 10 : 1
                                }}
                              />
                              
                              {/* Label text */}
                              <text
                                x={labelX}
                                y={labelY + 3}
                                textAnchor="middle"
                                fontSize="10"
                                fill="var(--text-primary)"
                                className="pointer-events-none transition-all duration-200 font-medium"
                                style={{
                                  opacity: isHighlighted || selectedConnection === connectionId ? 1 : 0.9,
                                  fontWeight: selectedConnection === connectionId ? 'bold' : 
                                             hoveredConnection === connectionId ? '600' : '500',
                                  zIndex: isHighlighted ? 10 : 1
                                }}
                              >
                                {connectionLabel}
                              </text>
                            </g>
                          );
                        })()}
                      </g>
                    );
                  })
                )})()}
                
                {/* Preview connection when hovering over unconnected node */}
                {previewConnection && (() => {
                  const fromNode = layoutNodes.find(n => n.id === previewConnection.fromId);
                  const toNode = layoutNodes.find(n => n.id === previewConnection.toId);
                  
                  if (!fromNode || !toNode) return null;
                  
                  // Calculate node centers
                  const fromCenterX = containerWidth / 2 + fromNode.x;
                  const fromCenterY = fromNode.y + 20 + 32;
                  const toCenterX = containerWidth / 2 + toNode.x;
                  const toCenterY = toNode.y + 20 + 32;
                  
                  // Calculate connection points (same logic as regular connections)
                  const deltaX = toCenterX - fromCenterX;
                  const deltaY = toCenterY - fromCenterY;
                  
                  let fromX, fromY, toX, toY;
                  let fromDirection, toDirection;
                  
                  const circleRadius = 32;
                  
                  // Determine directions
                  if (Math.abs(deltaX) > Math.abs(deltaY)) {
                    fromDirection = deltaX > 0 ? 'right' : 'left';
                    toDirection = deltaX > 0 ? 'left' : 'right';
                  } else {
                    fromDirection = deltaY > 0 ? 'down' : 'up';
                    toDirection = deltaY > 0 ? 'up' : 'down';
                  }
                  
                  // Calculate connection points
                  switch (fromDirection) {
                    case 'right':
                      fromX = fromCenterX + circleRadius;
                      fromY = fromCenterY;
                      break;
                    case 'left':
                      fromX = fromCenterX - circleRadius;
                      fromY = fromCenterY;
                      break;
                    case 'down':
                      fromX = fromCenterX;
                      fromY = fromCenterY + circleRadius;
                      break;
                    case 'up':
                      fromX = fromCenterX;
                      fromY = fromCenterY - circleRadius;
                      break;
                  }
                  
                  switch (toDirection) {
                    case 'right':
                      toX = toCenterX + circleRadius;
                      toY = toCenterY;
                      break;
                    case 'left':
                      toX = toCenterX - circleRadius;
                      toY = toCenterY;
                      break;
                    case 'down':
                      toX = toCenterX;
                      toY = toCenterY + circleRadius;
                      break;
                    case 'up':
                      toX = toCenterX;
                      toY = toCenterY - circleRadius;
                      break;
                  }
                  
                  // Create path
                  const connectionDistance = Math.sqrt((toX - fromX) ** 2 + (toY - fromY) ** 2);
                  let pathData;
                  
                  if (connectionDistance < 50) {
                    pathData = `M ${fromX} ${fromY} L ${toX} ${toY}`;
                  } else {
                    const curveLength = Math.min(connectionDistance * 0.4, 60);
                    
                    let cp1X = fromX;
                    let cp1Y = fromY;
                    
                    switch (fromDirection) {
                      case 'right':
                        cp1X = fromX + curveLength;
                        break;
                      case 'left':
                        cp1X = fromX - curveLength;
                        break;
                      case 'down':
                        cp1Y = fromY + curveLength;
                        break;
                      case 'up':
                        cp1Y = fromY - curveLength;
                        break;
                    }
                    
                    let cp2X = toX;
                    let cp2Y = toY;
                    
                    switch (toDirection) {
                      case 'right':
                        cp2X = toX + curveLength;
                        break;
                      case 'left':
                        cp2X = toX - curveLength;
                        break;
                      case 'down':
                        cp2Y = toY + curveLength;
                        break;
                      case 'up':
                        cp2Y = toY - curveLength;
                        break;
                    }
                    
                    pathData = `M ${fromX} ${fromY} C ${cp1X} ${cp1Y}, ${cp2X} ${cp2Y}, ${toX} ${toY}`;
                  }
                  
                  // Calculate arrow position and angle
                  let midX, midY, angle;
                  
                  if (pathData.includes('C')) {
                    // For curved paths
                    const pathParts = pathData.split(' ');
                    if (pathParts.length >= 7) {
                      const x1 = parseFloat(pathParts[1]);
                      const y1 = parseFloat(pathParts[2]);
                      const cp1x = parseFloat(pathParts[4]);
                      const cp1y = parseFloat(pathParts[5].replace(',', ''));
                      const cp2x = parseFloat(pathParts[6]);
                      const cp2y = parseFloat(pathParts[7].replace(',', ''));
                      const x2 = parseFloat(pathParts[8]);
                      const y2 = parseFloat(pathParts[9]);
                      
                      const t = 0.5;
                      midX = Math.pow(1-t, 3) * x1 + 3 * Math.pow(1-t, 2) * t * cp1x + 3 * (1-t) * Math.pow(t, 2) * cp2x + Math.pow(t, 3) * x2;
                      midY = Math.pow(1-t, 3) * y1 + 3 * Math.pow(1-t, 2) * t * cp1y + 3 * (1-t) * Math.pow(t, 2) * cp2y + Math.pow(t, 3) * y2;
                      
                      const tangentX = 3 * Math.pow(1-t, 2) * (cp1x - x1) + 6 * (1-t) * t * (cp2x - cp1x) + 3 * Math.pow(t, 2) * (x2 - cp2x);
                      const tangentY = 3 * Math.pow(1-t, 2) * (cp1y - y1) + 6 * (1-t) * t * (cp2y - cp1y) + 3 * Math.pow(t, 2) * (y2 - cp2y);
                      
                      angle = Math.atan2(tangentY, tangentX) * 180 / Math.PI;
                    } else {
                      midX = (fromX + toX) / 2;
                      midY = (fromY + toY) / 2;
                      angle = Math.atan2(toY - fromY, toX - fromX) * 180 / Math.PI;
                    }
                  } else {
                    midX = (fromX + toX) / 2;
                    midY = (fromY + toY) / 2;
                    angle = Math.atan2(toY - fromY, toX - fromX) * 180 / Math.PI;
                  }
                  
                  return (
                    <g key="preview-connection">
                      {/* Preview connection line - dashed green */}
                      <path
                        d={pathData}
                        stroke="var(--success)"
                        strokeWidth="3"
                        fill="none"
                        strokeDasharray="8,4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="pointer-events-none opacity-80"
                      />
                      
                      {/* Preview arrow */}
                      <polygon
                        points="0,-6 16,0 0,6"
                        fill="var(--success)"
                        stroke="var(--success)"
                        strokeWidth="0.5"
                        transform={`translate(${midX}, ${midY}) rotate(${angle})`}
                        className="pointer-events-none opacity-80"
                      />
                    </g>
                  );
                })()}
              </svg>

              {/* Nodes positioned relatively */}
              {layoutNodes.map((node) => {
                // Check if this node is part of the hovered connection
                const isPartOfHoveredConnection = hoveredConnection && 
                  (hoveredConnection.startsWith(`${node.id}-`) || hoveredConnection.endsWith(`-${node.id}`));
                
                const isNodeHighlighted = hoveredNode === node.id || isPartOfHoveredConnection;
                const isSelected = selectedNode === node.id;
                const isExpanded = expandedNode === node.id;
                const hasConnections = node.connected_to.length > 0 || 
                                     layoutNodes.some(n => n.connected_to.includes(node.id));
                
                return (
                  <div 
                    key={node.id}
                    className={`absolute flex flex-col items-center transition-all duration-200 ${
                      isExpanded ? 'pointer-events-none opacity-30 scale-90' :
                      draggedNode === node.id ? 'scale-110 z-10' : 'hover:scale-105'
                    } ${isNodeHighlighted ? 'z-20' : ''}`}
                    style={{ 
                      left: `${containerWidth / 2 + node.x - 32}px`, // Adjusted for 64px nodes (32 = half width)
                      top: `${node.y + 20}px`,
                      // Fade nodes when hovering other nodes (unless connected or selected) or when any node is expanded
                      // Also don't fade if this node is part of a hovered connection
                      opacity: expandedNode ? (isExpanded ? 0.3 : 0.1) :
                               expandedConnection ? (isPartOfHoveredConnection ? 1 : 0.3) :
                               hoveredNode && !isNodeHighlighted && !isSelected && 
                               !node.connected_to.includes(hoveredNode) && 
                               !layoutNodes.find(n => n.id === hoveredNode)?.connected_to.includes(node.id) ? 0.4 :
                               hoveredConnection && !isPartOfHoveredConnection ? 0.4 : 1
                    }}
                    onMouseDown={(e) => !isExpanded && handleMouseDown(e, node)}
                    onMouseEnter={() => {
                      if (!expandedNode) {
                        setHoveredNode(node.id);
                        // Handle preview connections when a node is selected
                        if (selectedNode && selectedNode !== node.id) {
                          const sourceNode = layoutNodes.find(n => n.id === selectedNode);
                          const willDelete = sourceNode?.connected_to.includes(node.id);
                          if (willDelete) {
                            // Show red effect for existing connection (will delete)
                            setHoveredConnectionToDelete(`${selectedNode}-${node.id}`);
                          } else {
                            // Show green preview for new connection
                            setPreviewConnection({
                              fromId: selectedNode,
                              toId: node.id
                            });
                          }
                        }
                      }
                    }}
                    onMouseLeave={() => {
                      if (!expandedNode) {
                        setHoveredNode(null);
                        setHoveredConnectionToDelete(null);
                        setPreviewConnection(null);
                      }
                    }}
                    onClick={(e) => !isExpanded && handleNodeClick(e, node)}
                  >
                    {/* Connection indicators - show for selected and hovered nodes */}
                    {!isExpanded && (isNodeHighlighted || isSelected) && hasConnections && (
                      <div 
                        className="absolute -inset-2 border-2 border-dashed rounded-full opacity-50 animate-pulse"
                        style={{
                          borderColor: 'var(--primary)'
                        }}
                      ></div>
                    )}
                    
                    {/* Selection indicator for connection mode */}
                    {!isExpanded && isSelected && (
                      <div 
                        className="absolute -inset-1 border-2 rounded-full"
                        style={{ borderColor: 'var(--secondary)' }}
                      ></div>
                    )}
                    
                    {/* Connectable indicator when another node is selected */}
                    {!isExpanded && selectedNode && selectedNode !== node.id && (
                      (() => {
                        const sourceNode = layoutNodes.find(n => n.id === selectedNode);
                        const willDelete = sourceNode?.connected_to.includes(node.id);
                        return (
                          <div 
                            className={`absolute -inset-2 border-2 rounded-full opacity-60 ${
                              willDelete ? 'animate-pulse' : ''
                            }`}
                            style={{
                              borderColor: willDelete ? 'var(--danger)' : 'var(--bg-hover)'
                            }}
                          ></div>
                        );
                      })()
                    )}
                    
                    {/* Themed circles for consistent styling with full name inside */}
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center font-semibold text-xs shadow-lg border-2 transition-all relative hover:scale-105"
                      style={{
                        backgroundColor: 'var(--primary)',
                        borderColor: 'var(--primary)',
                        color: 'var(--bg-primary)',
                        transform: isNodeHighlighted 
                          ? 'scale(1.05)' 
                          : 'scale(1)',
                        boxShadow: `0 4px 14px -2px var(--primary)30`
                      }}
                    >
                      <span className="text-center leading-tight px-1 break-words">
                        {node.name.length > 8 ? node.name.substring(0, 8) + '...' : node.name}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Expanded Edit Modal */}
      {expandedNode && (() => {
        const node = layoutNodes.find(n => n.id === expandedNode);
        if (!node) return null;

        return (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center" 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setExpandedNode(null);
                setEditingNodeName('');
                setEditingConnectionName(null);
              }
            }}
            tabIndex={-1}
          >
            {/* Backdrop blur effect */}
            <div className="absolute inset-0 backdrop-blur-sm" onClick={() => {
              setExpandedNode(null);
              setEditingNodeName('');
              setEditingConnectionName(null);
            }}></div>
            
            {/* Expanded node modal */}
            <div 
              className="relative z-10 rounded-2xl shadow-2xl p-8 min-w-[400px] max-w-[600px] w-full mx-4"
              style={{ 
                backgroundColor: 'var(--bg-primary)',
                border: '2px solid var(--border-color)'
              }}
            >
              {/* Node circle - large version */}
              <div className="flex flex-col items-center mb-6">
                <div 
                  className="w-24 h-24 rounded-full flex items-center justify-center font-bold text-lg shadow-lg border-4 transition-all"
                  style={{
                    backgroundColor: 'var(--primary)',
                    borderColor: 'var(--primary)',
                    color: 'var(--bg-primary)',
                    boxShadow: '0 8px 25px -5px var(--primary)30'
                  }}
                >
                  <span className="text-center leading-tight px-2 break-words">
                    {editingNodeName.length > 10 ? editingNodeName.substring(0, 10) + '...' : editingNodeName}
                  </span>
                </div>
                
                {/* Editable node name */}
                <div className="mt-4 w-full max-w-xs">
                  <input
                    type="text"
                    value={editingNodeName}
                    onChange={(e) => setEditingNodeName(e.target.value)}
                    onBlur={() => {
                      if (editingNodeName.trim() && editingNodeName.trim() !== node.name) {
                        updateNodeName(node.id, editingNodeName.trim());
                      }
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.target.blur();
                      }
                    }}
                    className="w-full px-3 py-2 text-center border border-[var(--border-color)] rounded-md bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                    placeholder="Node name..."
                  />
                </div>
                
                {/* Action buttons */}
                <div className="flex gap-4 mt-4">
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete "${node.name}" and all its connections?`)) {
                        deleteNodeAndConnections(node.id);
                        setExpandedNode(null);
                      }
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:scale-105"
                    style={{ 
                      backgroundColor: 'var(--danger)',
                      color: 'var(--bg-primary)'
                    }}
                    title="Delete node"
                  >
                    <FaTrash className="w-4 h-4" />
                    <span className="text-sm font-medium">Delete</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowAddNodeModal(true);
                      setNewNodeModalName('');
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:scale-105"
                    style={{ 
                      backgroundColor: 'var(--success)',
                      color: 'var(--bg-primary)'
                    }}
                    title="Add connected node"
                  >
                    <FaPlus className="w-4 h-4" />
                    <span className="text-sm font-medium">Add Node</span>
                  </button>
                </div>
              </div>

              {/* Connections list */}
              <div>
                <h4 className="text-lg font-semibold text-[var(--text-primary)] mb-4 text-center">
                  Connections
                </h4>
                
                {node.connected_to.length === 0 ? (
                  <div className="text-center py-6 text-[var(--text-secondary)]">
                    <p>No connections yet</p>
                    <p className="text-sm mt-1">Click outside to close and start connecting nodes</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {node.connected_to.map((targetId) => {
                      const targetNode = layoutNodes.find(n => n.id === targetId);
                      if (!targetNode) return null;
                      
                      const connectionName = node.connections?.[targetId] || '';
                      const isEditingThisConnection = editingConnectionName === `${node.id}-${targetId}`;
                      
                      return (
                        <div 
                          key={targetId}
                          className="flex items-center gap-3 p-3 rounded-lg border transition-all hover:shadow-md"
                          style={{ 
                            backgroundColor: 'var(--bg-secondary)',
                            borderColor: 'var(--border-color)'
                          }}
                        >
                          {/* Arrow indicator */}
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: 'var(--secondary)' }}
                          >
                            <span style={{ color: 'var(--bg-primary)' }}>→</span>
                          </div>
                          
                          {/* Target node info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-[var(--text-primary)]">
                                {targetNode.name}
                              </span>
                            </div>
                            
                            {/* Connection name input */}
                            {isEditingThisConnection ? (
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={connectionName}
                                  onChange={(e) => {
                                    // Update local state immediately for responsive UI
                                    const updatedNodes = nodes.map(n => {
                                      if (n.id === node.id) {
                                        return {
                                          ...n,
                                          connections: {
                                            ...n.connections,
                                            [targetId]: e.target.value
                                          }
                                        };
                                      }
                                      return n;
                                    });
                                    updateData({ ...data, nodes: updatedNodes });
                                  }}
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      setEditingConnectionName(null);
                                    }
                                  }}
                                  onBlur={() => setEditingConnectionName(null)}
                                  placeholder="Connection label..."
                                  className="flex-1 px-2 py-1 text-sm border rounded"
                                  style={{
                                    backgroundColor: 'var(--bg-primary)',
                                    borderColor: 'var(--border-color)',
                                    color: 'var(--text-primary)'
                                  }}
                                  autoFocus
                                />
                                <button
                                  onClick={() => setEditingConnectionName(null)}
                                  className="px-2 py-1 text-sm rounded"
                                  style={{ color: 'var(--text-secondary)' }}
                                >
                                  <FaCheck className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setEditingConnectionName(`${node.id}-${targetId}`)}
                                className="text-sm text-left hover:underline"
                                style={{ 
                                  color: connectionName ? 'var(--text-primary)' : 'var(--text-secondary)'
                                }}
                              >
                                {connectionName || 'Click to add label...'}
                              </button>
                            )}
                          </div>
                          
                          {/* Remove connection button */}
                          <button
                            onClick={() => {
                              toggleConnection(node.id, targetId);
                              // Update local expanded node data
                              const updatedNode = layoutNodes.find(n => n.id === expandedNode);
                              if (updatedNode && !updatedNode.connected_to.includes(targetId)) {
                                // Connection was removed, close expanded mode if no more connections
                                if (updatedNode.connected_to.length === 0) {
                                  setExpandedNode(null);
                                }
                              }
                            }}
                            className="p-2 rounded hover:bg-[var(--bg-hover)] transition-colors"
                            style={{ color: 'var(--danger)' }}
                            title="Remove connection"
                          >
                            <FaTimes className="w-3 h-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              
              {/* Close button */}
              <button
                onClick={() => {
                  setExpandedNode(null);
                  setEditingNodeName('');
                  setEditingConnectionName(null);
                }}
                className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-secondary)'
                }}
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>
          </div>
        );
      })()}

      {/* Expanded Connection Modal */}
      {expandedConnection && (() => {
        const { fromNodeId, toNodeId, connectionId } = expandedConnection;
        const fromNode = layoutNodes.find(n => n.id === fromNodeId);
        const toNode = layoutNodes.find(n => n.id === toNodeId);
        
        if (!fromNode || !toNode) return null;

        const connectionLabel = fromNode.connections?.[toNodeId] || '';

        return (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center" 
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setExpandedConnection(null);
                setLocalConnectionLabel('');
              }
            }}
            tabIndex={-1}
          >
            {/* Backdrop blur effect */}
            <div className="absolute inset-0 backdrop-blur-sm" onClick={() => {
              setExpandedConnection(null);
              setLocalConnectionLabel('');
            }}></div>
            
            {/* Connection modal */}
            <div 
              className="relative z-10 rounded-2xl shadow-2xl p-8 min-w-[400px] max-w-[600px] w-full mx-4"
              style={{ 
                backgroundColor: 'var(--bg-primary)',
                border: '2px solid var(--border-color)'
              }}
            >
              {/* Connection header */}
              <div className="flex flex-col items-center mb-6">
                <h3 className="text-xl font-bold text-[var(--text-primary)] mb-4">
                  Connection Details
                </h3>
                
                {/* Visual connection representation */}
                <div className="flex items-center gap-4 mb-4">
                  {/* From node */}
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center font-semibold text-sm shadow-lg border-2"
                      style={{
                        backgroundColor: 'var(--primary)',
                        borderColor: 'var(--primary)',
                        color: 'var(--bg-primary)',
                      }}
                    >
                      <span className="text-center leading-tight px-1 break-words">
                        {fromNode.name.length > 6 ? fromNode.name.substring(0, 6) + '...' : fromNode.name}
                      </span>
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">From</p>
                  </div>
                  
                  {/* Arrow and label */}
                  <div className="flex flex-col items-center">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-0.5" style={{ backgroundColor: 'var(--secondary)' }}></div>
                      <div className="w-0 h-0 border-l-8 border-r-0 border-t-4 border-b-4 border-l-[var(--secondary)] border-t-transparent border-b-transparent"></div>
                      <div className="w-8 h-0.5" style={{ backgroundColor: 'var(--secondary)' }}></div>
                    </div>
                    {connectionLabel && (
                      <p className="text-sm text-[var(--text-primary)] mt-1 font-medium">
                        "{connectionLabel}"
                      </p>
                    )}
                  </div>
                  
                  {/* To node */}
                  <div className="flex flex-col items-center">
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center font-semibold text-sm shadow-lg border-2"
                      style={{
                        backgroundColor: 'var(--bg-primary)',
                        borderColor: 'var(--secondary)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      <span className="text-center leading-tight px-1 break-words">
                        {toNode.name.length > 6 ? toNode.name.substring(0, 6) + '...' : toNode.name}
                      </span>
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">To</p>
                  </div>
                </div>
              </div>

              {/* Connection label editing */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  Connection Label
                </label>
                <input
                  type="text"
                  value={localConnectionLabel}
                  onChange={(e) => setLocalConnectionLabel(e.target.value)}
                  placeholder="Enter connection label..."
                  className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                />
              </div>

              {/* Action buttons */}
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => {
                    if (window.confirm(`Delete connection from "${fromNode.name}" to "${toNode.name}"?`)) {
                      const updatedNodes = nodes.map(n => {
                        if (n.id === fromNodeId) {
                          const newConnections = { ...n.connections };
                          delete newConnections[toNodeId];
                          return {
                            ...n,
                            connected_to: n.connected_to.filter(id => id !== toNodeId),
                            connections: newConnections
                          };
                        }
                        return n;
                      });
                      updateData({ ...data, nodes: updatedNodes });
                      setExpandedConnection(null);
                      setLocalConnectionLabel('');
                    }
                  }}
                  className="flex items-center gap-2 px-6 py-2 rounded-lg transition-all hover:scale-105"
                  style={{ 
                    backgroundColor: 'var(--danger)',
                    color: 'var(--bg-primary)'
                  }}
                >
                  <FaTrash className="w-4 h-4" />
                  <span className="font-medium">Delete Connection</span>
                </button>
                
                <button
                  onClick={() => {
                    setExpandedConnection(null);
                    setLocalConnectionLabel('');
                  }}
                  className="flex items-center gap-2 px-6 py-2 rounded-lg transition-all hover:scale-105"
                  style={{ 
                    backgroundColor: 'var(--secondary)',
                    color: 'var(--bg-primary)'
                  }}
                >
                  <span className="font-medium">Close</span>
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add Node Modal */}
      {showAddNodeModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center" 
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setShowAddNodeModal(false);
              setNewNodeModalName('');
            }
          }}
          tabIndex={-1}
        >
          {/* Backdrop blur effect */}
          <div className="absolute inset-0 backdrop-blur-sm" onClick={() => {
            setShowAddNodeModal(false);
            setNewNodeModalName('');
          }}></div>
          
          {/* Add Node modal */}
          <div 
            className="relative z-10 rounded-2xl shadow-2xl p-8 min-w-[400px] max-w-[500px] w-full mx-4"
            style={{ 
              backgroundColor: 'var(--bg-primary)',
              border: '2px solid var(--border-color)'
            }}
          >
            {/* Modal header */}
            <div className="flex flex-col items-center mb-6">
              <h3 className="text-xl font-bold text-[var(--text-primary)] mb-2">
                Add New Node
              </h3>
              <p className="text-sm text-[var(--text-secondary)] text-center">
                {expandedNode ? 'This node will be connected to the current node' : 'Create a new node in your flow diagram'}
              </p>
            </div>

            {/* Node name input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Node Name
              </label>
              <input
                type="text"
                value={newNodeModalName}
                onChange={(e) => setNewNodeModalName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newNodeModalName.trim()) {
                    // Create the node
                    const newNode = {
                      id: generateId(),
                      name: newNodeModalName.trim(),
                      connected_to: []
                    };
                    
                    if (expandedNode) {
                      // Connect to the expanded node
                      const updatedNodes = nodes.map(n => 
                        n.id === expandedNode 
                          ? { ...n, connected_to: [...n.connected_to, newNode.id] }
                          : n
                      );
                      updateData({
                        ...data,
                        nodes: [...updatedNodes, newNode]
                      });
                      setExpandedNode(null);
                    } else {
                      // Just add the node
                      updateData({
                        ...data,
                        nodes: [...nodes, newNode]
                      });
                    }
                    
                    setShowAddNodeModal(false);
                    setNewNodeModalName('');
                  }
                }}
                placeholder="Enter node name..."
                className="w-full px-3 py-2 border border-[var(--border-color)] rounded-md bg-[var(--bg-secondary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                autoFocus
              />
            </div>

            {/* Action buttons */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => {
                  setShowAddNodeModal(false);
                  setNewNodeModalName('');
                }}
                className="flex items-center gap-2 px-6 py-2 rounded-lg transition-all hover:scale-105"
                style={{ 
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)'
                }}
              >
                <span className="font-medium">Cancel</span>
              </button>
              
              <button
                onClick={() => {
                  if (!newNodeModalName.trim()) return;
                  
                  // Create the node
                  const newNode = {
                    id: generateId(),
                    name: newNodeModalName.trim(),
                    connected_to: []
                  };
                  
                  if (expandedNode) {
                    // Connect to the expanded node
                    const updatedNodes = nodes.map(n => 
                      n.id === expandedNode 
                        ? { ...n, connected_to: [...n.connected_to, newNode.id] }
                        : n
                    );
                    updateData({
                      ...data,
                      nodes: [...updatedNodes, newNode]
                    });
                    setExpandedNode(null);
                  } else {
                    // Just add the node
                    updateData({
                      ...data,
                      nodes: [...nodes, newNode]
                    });
                  }
                  
                  setShowAddNodeModal(false);
                  setNewNodeModalName('');
                }}
                disabled={!newNodeModalName.trim()}
                className="flex items-center gap-2 px-6 py-2 rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ 
                  backgroundColor: 'var(--primary)',
                  color: 'var(--bg-primary)'
                }}
              >
                <FaPlus className="w-4 h-4" />
                <span className="font-medium">Create Node</span>
              </button>
            </div>
            
            {/* Close button */}
            <button
              onClick={() => {
                setShowAddNodeModal(false);
                setNewNodeModalName('');
              }}
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
              style={{
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-secondary)'
              }}
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Right: Node Details & Connections - Only show when there's enough space */}
      {showSidebar && (
        <div className="w-1/3 pl-4">
        {isFullscreen ? (
          // Fullscreen mode - enhanced sidebar
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-[var(--text-primary)]">
              Flow Diagram Manager
            </h3>
            
            {/* Add new node section */}
            <div className="border border-[var(--border-color)] rounded-lg p-4 bg-[var(--bg-secondary)]">
              <h4 className="font-medium text-[var(--text-primary)] mb-3">Add New Node</h4>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Node name..."
                  value={newNodeName}
                  onChange={(e) => setNewNodeName(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addNode()}
                  className="flex-1 px-3 py-2 border border-[var(--border-color)] rounded-md bg-[var(--bg-primary)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
                />
                <button
                  onClick={addNode}
                  disabled={!newNodeName.trim()}
                  className="px-4 py-2 rounded-md hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm transition-opacity"
                  style={{ 
                    backgroundColor: 'var(--primary)', 
                    color: 'var(--bg-primary)' 
                  }}
                  title="Add node"
                >
                  <FaPlus className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Node/Connection Details */}
            {editingNode ? (
              <div className="space-y-4">
                {layoutNodes.filter(n => n.id === editingNode).map((node) => (
                  <div key={node.id} className="border border-[var(--border-color)] rounded-lg p-6 bg-[var(--bg-secondary)]">
                    {/* Node circle - large version */}
                    <div className="flex flex-col items-center mb-6">
                      <div 
                        className="w-24 h-24 rounded-full flex items-center justify-center font-bold text-lg shadow-lg border-4 transition-all"
                        style={{
                          backgroundColor: 'var(--primary)',
                          borderColor: 'var(--primary)',
                          color: 'var(--bg-primary)',
                          boxShadow: '0 8px 25px -5px var(--primary)30'
                        }}
                      >
                        <span className="text-center leading-tight px-2 break-words">
                          {node.name.length > 10 ? node.name.substring(0, 10) + '...' : node.name}
                        </span>
                      </div>
                      
                      {/* Editable node name */}
                      <div className="mt-4 w-full max-w-xs">
                        <input
                          type="text"
                          value={localNodeName}
                          onChange={(e) => setLocalNodeName(e.target.value)}
                          onBlur={() => {
                            if (localNodeName.trim() && localNodeName.trim() !== node.name) {
                              updateNodeName(node.id, localNodeName.trim());
                            }
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.target.blur();
                            }
                          }}
                          className="w-full px-3 py-2 text-center border border-[var(--border-color)] rounded-md bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                          placeholder="Node name..."
                        />
                      </div>
                      
                      {/* Action buttons */}
                      <div className="flex gap-4 mt-4">
                        <button
                          onClick={() => {
                            if (window.confirm(`Delete "${node.name}" and all its connections?`)) {
                              deleteNode(node.id);
                              setEditingNode(null);
                              setSelectedNode(null);
                            }
                          }}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:scale-105"
                          style={{ 
                            backgroundColor: 'var(--danger)',
                            color: 'var(--bg-primary)'
                          }}
                          title="Delete node"
                        >
                          <FaTrash className="w-4 h-4" />
                          <span className="text-sm font-medium">Delete</span>
                        </button>
                        
                        <button
                          onClick={() => {
                            setShowAddNodeModal(true);
                            setNewNodeModalName('');
                          }}
                          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all hover:scale-105"
                          style={{ 
                            backgroundColor: 'var(--success)',
                            color: 'var(--bg-primary)'
                          }}
                          title="Add connected node"
                        >
                          <FaPlus className="w-4 h-4" />
                          <span className="text-sm font-medium">Add Node</span>
                        </button>
                      </div>
                    </div>

                    {/* Connections list */}
                    <div>
                      <h4 className="text-lg font-semibold text-[var(--text-primary)] mb-4 text-center">
                        Connections
                      </h4>
                      
                      {node.connected_to.length === 0 ? (
                        <div className="text-center py-6 text-[var(--text-secondary)]">
                          <p>No connections yet</p>
                          <p className="text-sm mt-1">Select another node in the diagram to create a connection</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {node.connected_to.map((targetId) => {
                            const targetNode = layoutNodes.find(n => n.id === targetId);
                            if (!targetNode) return null;
                            
                            const connectionName = node.connections?.[targetId] || '';
                            const isEditingThisConnection = editingConnectionName === `${node.id}-${targetId}`;
                            
                            return (
                              <div 
                                key={targetId}
                                className="flex items-center gap-3 p-3 rounded-lg border transition-all hover:shadow-md"
                                style={{ 
                                  backgroundColor: 'var(--bg-primary)',
                                  borderColor: 'var(--border-color)'
                                }}
                              >
                                {/* Arrow indicator */}
                                <div 
                                  className="w-8 h-8 rounded-full flex items-center justify-center"
                                  style={{ backgroundColor: 'var(--secondary)' }}
                                >
                                  <span style={{ color: 'var(--bg-primary)' }}>→</span>
                                </div>
                                
                                {/* Target node info */}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="font-medium text-[var(--text-primary)]">
                                      {targetNode.name}
                                    </span>
                                  </div>
                                  
                                  {/* Connection name input */}
                                  {isEditingThisConnection ? (
                                    <div className="flex gap-2">
                                      <input
                                        type="text"
                                        value={connectionName}
                                        onChange={(e) => {
                                          updateConnectionName(node.id, targetId, e.target.value);
                                        }}
                                        onKeyPress={(e) => {
                                          if (e.key === 'Enter') {
                                            setEditingConnectionName(null);
                                          }
                                        }}
                                        onBlur={() => setEditingConnectionName(null)}
                                        placeholder="Connection label..."
                                        className="flex-1 px-2 py-1 text-sm border rounded"
                                        style={{
                                          backgroundColor: 'var(--bg-secondary)',
                                          borderColor: 'var(--border-color)',
                                          color: 'var(--text-primary)'
                                        }}
                                        autoFocus
                                      />
                                      <button
                                        onClick={() => setEditingConnectionName(null)}
                                        className="px-2 py-1 text-sm rounded"
                                        style={{ color: 'var(--text-secondary)' }}
                                      >
                                        <FaCheck className="w-3 h-3" />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setEditingConnectionName(`${node.id}-${targetId}`)}
                                      className="text-sm text-left hover:underline"
                                      style={{ 
                                        color: connectionName ? 'var(--text-primary)' : 'var(--text-secondary)'
                                      }}
                                    >
                                      {connectionName || 'Click to add label...'}
                                    </button>
                                  )}
                                </div>
                                
                                {/* Remove connection button */}
                                <button
                                  onClick={() => toggleConnection(node.id, targetId)}
                                  className="p-2 rounded hover:bg-[var(--bg-hover)] transition-colors"
                                  style={{ color: 'var(--danger)' }}
                                  title="Remove connection"
                                >
                                  <FaTimes className="w-3 h-3" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : selectedConnection ? (
              <div className="space-y-4">
                {(() => {
                  const [fromNodeId, toNodeId] = selectedConnection.split('-');
                  const fromNode = layoutNodes.find(n => n.id === fromNodeId);
                  const toNode = layoutNodes.find(n => n.id === toNodeId);
                  
                  if (!fromNode || !toNode) return null;
                  
                  const connectionName = fromNode.connections?.[toNodeId] || '';
                  
                  return (
                    <div className="border border-[var(--border-color)] rounded-lg p-4 bg-[var(--bg-secondary)]">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-[var(--text-primary)]">
                          Connection Details
                        </h4>
                        <button
                          onClick={() => deleteConnection(fromNodeId, toNodeId)}
                          className="p-1 rounded transition-colors"
                          style={{ 
                            color: 'var(--text-secondary)' 
                          }}
                          onMouseEnter={(e) => e.target.style.color = 'var(--danger)'}
                          onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
                          title="Delete connection"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div className="text-sm">
                          <p className="text-[var(--text-secondary)] mb-1">From:</p>
                          <p className="text-[var(--text-primary)] font-medium">{fromNode.name}</p>
                        </div>
                        
                        <div className="text-sm">
                          <p className="text-[var(--text-secondary)] mb-1">To:</p>
                          <p className="text-[var(--text-primary)] font-medium">{toNode.name}</p>
                        </div>
                        
                        <div className="text-sm">
                          <p className="text-[var(--text-secondary)] mb-1">Connection Label:</p>
                          <input
                            type="text"
                            value={connectionName}
                            onChange={(e) => updateConnectionName(fromNodeId, toNodeId, e.target.value)}
                            placeholder="Enter connection label..."
                            className="w-full px-2 py-1 border border-[var(--border-color)] rounded bg-[var(--bg-primary)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : null}

            {/* All Nodes List */}
            <div className="border border-[var(--border-color)] rounded-lg p-4 bg-[var(--bg-secondary)]">
              <h4 className="font-medium text-[var(--text-primary)] mb-3">All Nodes ({layoutNodes.length})</h4>
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {layoutNodes.length === 0 ? (
                  <p className="text-sm text-[var(--text-secondary)] italic text-center py-4">
                    No nodes yet. Add your first node above!
                  </p>
                ) : (
                  layoutNodes.map((node) => (
                    <button
                      key={node.id}
                      onClick={() => {
                        setEditingNode(node.id);
                        setSelectedNode(node.id);
                        setSelectedConnection(null);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors border ${
                        editingNode === node.id 
                          ? 'bg-[var(--primary)] text-white border-[var(--primary)]' 
                          : 'bg-[var(--bg-primary)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)] border-[var(--border-color)]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{node.name}</span>
                        <span className="text-xs opacity-70">
                          {node.connected_to.length} connections
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : (
          // Regular mode - original sidebar
          <div>
            <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
              Node Details
            </h3>
            
            {editingNode ? (
              <div className="space-y-4">
                {layoutNodes.filter(n => n.id === editingNode).map((node) => (
                  <div key={node.id} className="border border-[var(--border-color)] rounded-lg p-4 bg-[var(--bg-secondary)]">
                    {/* Node Name */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-[var(--text-secondary)] mb-2">
                        Node Name
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={localNodeName}
                          onChange={(e) => setLocalNodeName(e.target.value)}
                          onBlur={() => {
                            if (localNodeName.trim() && localNodeName.trim() !== node.name) {
                              updateNodeName(node.id, localNodeName.trim());
                            }
                          }}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.target.blur();
                            }
                          }}
                          className="flex-1 px-3 py-2 border border-[var(--border-color)] rounded-md bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)]"
                          placeholder="Node name..."
                        />
                        <button
                          onClick={() => deleteNode(node.id)}
                          className="p-2 rounded transition-colors"
                          style={{ 
                            color: 'var(--text-secondary)' 
                          }}
                          onMouseEnter={(e) => e.target.style.color = 'var(--danger)'}
                          onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
                          title="Delete node"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Connections */}
                    <div>
                      <p className="text-sm text-[var(--text-secondary)] mb-3">
                        Connects to:
                      </p>
                      <div className="space-y-2">
                        {nodes.filter(n => n.id !== node.id).map((targetNode) => (
                          <label key={targetNode.id} className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              checked={node.connected_to.includes(targetNode.id)}
                              onChange={() => toggleConnection(node.id, targetNode.id)}
                              className="rounded border-[var(--border-color)] text-[var(--primary)] focus:ring-[var(--primary)]"
                            />
                            <span className="text-[var(--text-primary)]">
                              {targetNode.name}
                            </span>
                          </label>
                        ))}
                        {nodes.length <= 1 && (
                          <p className="text-xs text-[var(--text-secondary)] italic">
                            Add more nodes to create connections
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : selectedConnection ? (
              <div className="space-y-4">
                {(() => {
                  const [fromNodeId, toNodeId] = selectedConnection.split('-');
                  const fromNode = layoutNodes.find(n => n.id === fromNodeId);
                  const toNode = layoutNodes.find(n => n.id === toNodeId);
                  
                  if (!fromNode || !toNode) return null;
                  
                  const connectionName = fromNode.connections?.[toNodeId] || '';
                  
                  return (
                    <div className="border border-[var(--border-color)] rounded-lg p-4 bg-[var(--bg-secondary)]">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-medium text-[var(--text-primary)]">
                          Connection Details
                        </h4>
                        <button
                          onClick={() => deleteConnection(fromNodeId, toNodeId)}
                          className="p-1 rounded transition-colors"
                          style={{ 
                            color: 'var(--text-secondary)' 
                          }}
                          onMouseEnter={(e) => e.target.style.color = 'var(--danger)'}
                          onMouseLeave={(e) => e.target.style.color = 'var(--text-secondary)'}
                          title="Delete connection"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </div>

                      <div className="space-y-3">
                        <div className="text-sm">
                          <p className="text-[var(--text-secondary)] mb-1">From:</p>
                          <p className="text-[var(--text-primary)] font-medium">{fromNode.name}</p>
                        </div>
                        
                        <div className="text-sm">
                          <p className="text-[var(--text-secondary)] mb-1">To:</p>
                          <p className="text-[var(--text-primary)] font-medium">{toNode.name}</p>
                        </div>
                        
                        <div className="text-sm">
                          <p className="text-[var(--text-secondary)] mb-1">Connection Label:</p>
                          <input
                            type="text"
                            value={connectionName}
                            onChange={(e) => updateConnectionName(fromNodeId, toNodeId, e.target.value)}
                            placeholder="Enter connection label..."
                            className="w-full px-2 py-1 border border-[var(--border-color)] rounded bg-[var(--bg-primary)] text-[var(--text-primary)] text-sm focus:outline-none focus:ring-1 focus:ring-[var(--primary)]"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="text-center py-8 text-[var(--text-secondary)] bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)]">
                <p>Click on a node or connection in the diagram to edit</p>
              </div>
            )}

            {/* All Nodes List */}
            <div className="mt-6">
              <h4 className="font-medium text-[var(--text-primary)] mb-3">All Nodes</h4>
              <div className="space-y-2">
                {layoutNodes.map((node) => (
                  <button
                    key={node.id}
                    onClick={() => setEditingNode(node.id)}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors border ${
                      editingNode === node.id 
                        ? 'bg-[var(--primary)] text-white border-[var(--primary)]' 
                        : 'bg-[var(--bg-secondary)] text-[var(--text-primary)] hover:bg-[var(--bg-hover)] border-[var(--border-color)]'
                    }`}
                  >
                    {node.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
        </div>
      )}
    </div>
  );
}
